import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'

// Field names matching the at__get_measures RPC function response
const FIELD_FUNDS_ID = 'funds__id'
const FIELD_FUNDS_NAME = 'funds__name'
const FIELD_DATE_INVESTED = 'date_invested'
const FIELD_DATE_INVESTED_FMT = 'date_invested_fmt'
const FIELD_DATE_INVESTED_ORIGINAL = 'date_invested_original'
const FIELD_DATE_INVESTED_ORIGINAL_FMT = 'date_invested_original_fmt'
const FIELD_DATE_EXITED = 'date_exited'
const FIELD_DATE_EXITED_FMT = 'date_exited_fmt'
const FIELD_COST_INCURRED = 'cost_incurred'
const FIELD_COST_INCURRED_FMT = 'cost_incurred_fmt'
const FIELD_COST = 'cost'
const FIELD_COST_FMT = 'cost_fmt'
const FIELD_COST_HELD = 'cost_held'
const FIELD_COST_HELD_FMT = 'cost_held_fmt'
const FIELD_COST_DISPOSED = 'cost_disposed'
const FIELD_COST_DISPOSED_FMT = 'cost_disposed_fmt'
const FIELD_COST_CONVERTED_IN = 'cost_converted_in'
const FIELD_COST_CONVERTED_IN_FMT = 'cost_converted_in_fmt'
const FIELD_COST_CONVERTED_OUT = 'cost_converted_out'
const FIELD_COST_CONVERTED_OUT_FMT = 'cost_converted_out_fmt'
const FIELD_COST_DEDUCTED = 'cost_deducted'
const FIELD_COST_DEDUCTED_FMT = 'cost_deducted_fmt'
// Interest (docs/INTEREST_ACCRUAL_DESIGN.md section 5.4, 2026-10-14): the open
// accrued balance, the income recognised, the cash received as interest, the
// cost held that came from capitalised interest.
const FIELD_INTEREST_ACCRUED = 'interest_accrued'
const FIELD_INTEREST_ACCRUED_FMT = 'interest_accrued_fmt'
const FIELD_INTEREST_EARNED = 'interest_earned'
const FIELD_INTEREST_EARNED_FMT = 'interest_earned_fmt'
const FIELD_INTEREST_RECEIVED = 'interest_received'
const FIELD_INTEREST_RECEIVED_FMT = 'interest_received_fmt'
const FIELD_COST_CAPITALIZED = 'cost_capitalized'
const FIELD_COST_CAPITALIZED_FMT = 'cost_capitalized_fmt'
// The non-cash projections (2026-10-21, PR 1 of the asset-delta design,
// scripts/proposals/asset-delta-streams/DESIGN.md): the basis that arrived
// without cash and the gain or loss recognised without proceeds;
// realized_gain = proceeds - cost_disposed + gain_recognized.
const FIELD_COST_ADJUSTED = 'cost_adjusted'
const FIELD_COST_ADJUSTED_FMT = 'cost_adjusted_fmt'
const FIELD_GAIN_RECOGNIZED = 'gain_recognized'
const FIELD_GAIN_RECOGNIZED_FMT = 'gain_recognized_fmt'
const FIELD_PROCEEDS = 'proceeds'
const FIELD_PROCEEDS_FMT = 'proceeds_fmt'
const FIELD_FMV = 'fmv'
const FIELD_FMV_FMT = 'fmv_fmt'
const FIELD_REALIZED_GAIN = 'realized_gain'
const FIELD_REALIZED_GAIN_FMT = 'realized_gain_fmt'
const FIELD_UNREALIZED_GAIN = 'unrealized_gain'
const FIELD_UNREALIZED_GAIN_FMT = 'unrealized_gain_fmt'
const FIELD_MOIC = 'moic'
const FIELD_MOIC_FMT = 'moic_fmt'
const FIELD_UNIT_PRICE = 'unit_price'
const FIELD_UNIT_PRICE_FMT = 'unit_price_fmt'
const FIELD_NO_HELD = 'no_held'
const FIELD_NO_HELD_FMT = 'no_held_fmt'
const FIELD_NO_DISPOSED = 'no_disposed'
const FIELD_NO_DISPOSED_FMT = 'no_disposed_fmt'
const FIELD_PRINCIPAL_HELD = 'principal_held'
const FIELD_PRINCIPAL_HELD_FMT = 'principal_held_fmt'
const FIELD_PRINCIPAL_DISPOSED = 'principal_disposed'
const FIELD_PRINCIPAL_DISPOSED_FMT = 'principal_disposed_fmt'
const FIELD_UNITS_ACQUIRED = 'units_acquired'
const FIELD_UNITS_ACQUIRED_FMT = 'units_acquired_fmt'
const FIELD_UNITS_DISPOSED = 'units_disposed'
const FIELD_UNITS_DISPOSED_FMT = 'units_disposed_fmt'
const FIELD_UNITS_HELD = 'units_held'
const FIELD_UNITS_HELD_FMT = 'units_held_fmt'
const FIELD_UNITS_CONVERTED_IN = 'units_converted_in'
const FIELD_UNITS_CONVERTED_IN_FMT = 'units_converted_in_fmt'
const FIELD_UNITS_CONVERTED_OUT = 'units_converted_out'
const FIELD_UNITS_CONVERTED_OUT_FMT = 'units_converted_out_fmt'
const FIELD_ESCROW_BALANCE = 'escrow_balance'
const FIELD_ESCROW_BALANCE_FMT = 'escrow_balance_fmt'
const FIELD_TOTAL_VALUE = 'total_value'
const FIELD_TOTAL_VALUE_FMT = 'total_value_fmt'
const FIELD_RETURN = 'return'
const FIELD_RETURN_FMT = 'return_fmt'
const FIELD_PERC_EXITED = 'perc_exited'
const FIELD_PERC_EXITED_FMT = 'perc_exited_fmt'
const FIELD_CURRENCY_CODE = 'currency_code'
const FIELD_REALIZED_INVESTED = 'realized_invested'
const FIELD_REALIZED_INVESTED_FMT = 'realized_invested_fmt'
const FIELD_AVG_CHECK = 'avg_check'
const FIELD_AVG_CHECK_FMT = 'avg_check_fmt'
const FIELD_COMPANIES = 'companies'
const FIELD_COMPANIES_FMT = 'companies_fmt'
const FIELD_NO_EXITED = 'no_exited'
const FIELD_NO_EXITED_FMT = 'no_exited_fmt'
const FIELD_NO_ACTIVE = 'no_active'
const FIELD_NO_ACTIVE_FMT = 'no_active_fmt'
const FIELD_INVESTMENTS = 'investments'
const FIELD_INVESTMENTS_FMT = 'investments_fmt'
// Internal carrier appended by at__get_measures to every row: a map of
// {accounts__id: {cost, cost_disposed, units_disposed, units_held}} behind
// that row's distinct company counts, so a caller merging rows can count each
// company once. Not an output column — declared here only so the strict schema
// passes it through rather than rejecting the whole row.
const FIELD_COMPANY_STATS = '_company_stats'

export const InvestmentMeasureRecordSchema = z
  .object({
    [FIELD_FUNDS_ID]: z.string().nullish(),
    [FIELD_FUNDS_NAME]: z.string().nullish(),
    [FIELD_DATE_INVESTED]: z.string().nullish(),
    [FIELD_DATE_INVESTED_FMT]: z.string().nullish(),
    [FIELD_DATE_INVESTED_ORIGINAL]: z.string().nullish(),
    [FIELD_DATE_INVESTED_ORIGINAL_FMT]: z.string().nullish(),
    [FIELD_DATE_EXITED]: z.string().nullish(),
    [FIELD_DATE_EXITED_FMT]: z.string().nullish(),
    [FIELD_COST_INCURRED]: z.number().nullish(),
    [FIELD_COST_INCURRED_FMT]: z.string().nullish(),
    [FIELD_COST]: z.number().nullish(),
    [FIELD_COST_FMT]: z.string().nullish(),
    [FIELD_COST_HELD]: z.number().nullish(),
    [FIELD_COST_HELD_FMT]: z.string().nullish(),
    [FIELD_COST_DISPOSED]: z.number().nullish(),
    [FIELD_COST_DISPOSED_FMT]: z.string().nullish(),
    [FIELD_COST_CONVERTED_IN]: z.number().nullish(),
    [FIELD_COST_CONVERTED_IN_FMT]: z.string().nullish(),
    [FIELD_COST_CONVERTED_OUT]: z.number().nullish(),
    [FIELD_COST_CONVERTED_OUT_FMT]: z.string().nullish(),
    [FIELD_COST_DEDUCTED]: z.number().nullish(),
    [FIELD_COST_DEDUCTED_FMT]: z.string().nullish(),
    [FIELD_INTEREST_ACCRUED]: z.number().nullish(),
    [FIELD_INTEREST_ACCRUED_FMT]: z.string().nullish(),
    [FIELD_INTEREST_EARNED]: z.number().nullish(),
    [FIELD_INTEREST_EARNED_FMT]: z.string().nullish(),
    [FIELD_INTEREST_RECEIVED]: z.number().nullish(),
    [FIELD_INTEREST_RECEIVED_FMT]: z.string().nullish(),
    [FIELD_COST_CAPITALIZED]: z.number().nullish(),
    [FIELD_COST_CAPITALIZED_FMT]: z.string().nullish(),
    [FIELD_COST_ADJUSTED]: z.number().nullish(),
    [FIELD_COST_ADJUSTED_FMT]: z.string().nullish(),
    [FIELD_GAIN_RECOGNIZED]: z.number().nullish(),
    [FIELD_GAIN_RECOGNIZED_FMT]: z.string().nullish(),
    [FIELD_PROCEEDS]: z.number().nullish(),
    [FIELD_PROCEEDS_FMT]: z.string().nullish(),
    [FIELD_FMV]: z.number().nullish(),
    [FIELD_FMV_FMT]: z.string().nullish(),
    [FIELD_REALIZED_GAIN]: z.number().nullish(),
    [FIELD_REALIZED_GAIN_FMT]: z.string().nullish(),
    [FIELD_UNREALIZED_GAIN]: z.number().nullish(),
    [FIELD_UNREALIZED_GAIN_FMT]: z.string().nullish(),
    [FIELD_MOIC]: z.number().nullish(),
    [FIELD_MOIC_FMT]: z.string().nullish(),
    [FIELD_UNIT_PRICE]: z.number().nullish(),
    [FIELD_UNIT_PRICE_FMT]: z.string().nullish(),
    [FIELD_NO_HELD]: z.number().nullish(),
    [FIELD_NO_HELD_FMT]: z.string().nullish(),
    [FIELD_NO_DISPOSED]: z.number().nullish(),
    [FIELD_NO_DISPOSED_FMT]: z.string().nullish(),
    [FIELD_PRINCIPAL_HELD]: z.number().nullish(),
    [FIELD_PRINCIPAL_HELD_FMT]: z.string().nullish(),
    [FIELD_PRINCIPAL_DISPOSED]: z.number().nullish(),
    [FIELD_PRINCIPAL_DISPOSED_FMT]: z.string().nullish(),
    [FIELD_UNITS_ACQUIRED]: z.number().nullish(),
    [FIELD_UNITS_ACQUIRED_FMT]: z.string().nullish(),
    [FIELD_UNITS_DISPOSED]: z.number().nullish(),
    [FIELD_UNITS_DISPOSED_FMT]: z.string().nullish(),
    [FIELD_UNITS_HELD]: z.number().nullish(),
    [FIELD_UNITS_HELD_FMT]: z.string().nullish(),
    [FIELD_UNITS_CONVERTED_IN]: z.number().nullish(),
    [FIELD_UNITS_CONVERTED_IN_FMT]: z.string().nullish(),
    [FIELD_UNITS_CONVERTED_OUT]: z.number().nullish(),
    [FIELD_UNITS_CONVERTED_OUT_FMT]: z.string().nullish(),
    [FIELD_ESCROW_BALANCE]: z.number().nullish(),
    [FIELD_ESCROW_BALANCE_FMT]: z.string().nullish(),
    [FIELD_TOTAL_VALUE]: z.number().nullish(),
    [FIELD_TOTAL_VALUE_FMT]: z.string().nullish(),
    [FIELD_RETURN]: z.number().nullish(),
    [FIELD_RETURN_FMT]: z.string().nullish(),
    [FIELD_PERC_EXITED]: z.number().nullish(),
    [FIELD_PERC_EXITED_FMT]: z.string().nullish(),
    [FIELD_CURRENCY_CODE]: z.string().nullish(),
    [FIELD_REALIZED_INVESTED]: z.number().nullish(),
    [FIELD_REALIZED_INVESTED_FMT]: z.string().nullish(),
    [FIELD_AVG_CHECK]: z.number().nullish(),
    [FIELD_AVG_CHECK_FMT]: z.string().nullish(),
    [FIELD_COMPANIES]: z.number().nullish(),
    [FIELD_COMPANIES_FMT]: z.string().nullish(),
    [FIELD_NO_EXITED]: z.number().nullish(),
    [FIELD_NO_EXITED_FMT]: z.string().nullish(),
    [FIELD_NO_ACTIVE]: z.number().nullish(),
    [FIELD_NO_ACTIVE_FMT]: z.string().nullish(),
    [FIELD_INVESTMENTS]: z.number().nullish(),
    [FIELD_INVESTMENTS_FMT]: z.string().nullish(),
    [FIELD_COMPANY_STATS]: z.record(z.string(), z.unknown()).nullish()
  })
  .strict()

export const InvestmentMeasureRecordFields = makeFieldKeys(InvestmentMeasureRecordSchema)

export type InvestmentMeasureRecord = z.infer<typeof InvestmentMeasureRecordSchema>

export function validateInvestmentMeasureRecord(
  data: unknown,
  options?: { isArray?: boolean }
): InvestmentMeasureRecord | InvestmentMeasureRecord[] {
  try {
    return validateWithZodSchema(InvestmentMeasureRecordSchema, data, 'investment measure record', options) as
      | InvestmentMeasureRecord
      | InvestmentMeasureRecord[]
  } catch (error) {
    throw new Error(`Investment measure record validation failed: ${error}`)
  }
}
